import { supabase } from '../lib/supabase';

export interface AdminMobileNumberRow {
  user_id: string;
  mobile_phone: string;
  created_at: string;
  updated_at: string;
}

/** Digits only, for length checks. Keeps leading + out. */
export function phoneDigitsOnly(value: string): string {
  return String(value || '').replace(/\D/g, '');
}

/** Normalize whitespace; keep leading + if present. */
export function normalizeMobilePhone(value: string): string {
  const trimmed = String(value || '').trim().replace(/\s+/g, ' ');
  if (!trimmed) return '';
  if (trimmed.startsWith('+')) {
    return `+${phoneDigitsOnly(trimmed)}`;
  }
  return phoneDigitsOnly(trimmed) || trimmed;
}

export function isValidMobilePhone(value: string): boolean {
  return phoneDigitsOnly(value).length >= 7;
}

export async function getMyAdminMobileNumber(): Promise<string | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return null;

  const { data, error } = await supabase
    .from('admin_mobile_numbers')
    .select('mobile_phone')
    .eq('user_id', user.id)
    .maybeSingle();

  if (error) throw error;
  return data?.mobile_phone ?? null;
}

export async function saveMyAdminMobileNumber(mobilePhone: string): Promise<AdminMobileNumberRow> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error('You must be signed in to save a mobile number.');

  const normalized = normalizeMobilePhone(mobilePhone);
  if (!isValidMobilePhone(normalized)) {
    throw new Error('Enter a valid mobile number (at least 7 digits). Prefer +353… format.');
  }

  const { data, error } = await supabase
    .from('admin_mobile_numbers')
    .upsert(
      {
        user_id: user.id,
        mobile_phone: normalized,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'user_id' }
    )
    .select('*')
    .single();

  if (error) throw error;
  return data as AdminMobileNumberRow;
}

export async function clearMyAdminMobileNumber(): Promise<void> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error('You must be signed in.');

  const { error } = await supabase.from('admin_mobile_numbers').delete().eq('user_id', user.id);
  if (error) throw error;
}

export async function listAdminMobileNumbers(): Promise<AdminMobileNumberRow[]> {
  const { data, error } = await supabase
    .from('admin_mobile_numbers')
    .select('user_id, mobile_phone, created_at, updated_at')
    .order('updated_at', { ascending: false });

  if (error) throw error;
  return (data || []) as AdminMobileNumberRow[];
}

export async function saveAdminMobileNumberForUser(
  userId: string,
  mobilePhone: string
): Promise<AdminMobileNumberRow> {
  const normalized = normalizeMobilePhone(mobilePhone);
  if (!isValidMobilePhone(normalized)) {
    throw new Error('Enter a valid mobile number (at least 7 digits). Prefer +353… format.');
  }

  const { data, error } = await supabase
    .from('admin_mobile_numbers')
    .upsert(
      {
        user_id: userId,
        mobile_phone: normalized,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'user_id' }
    )
    .select('*')
    .single();

  if (error) throw error;
  return data as AdminMobileNumberRow;
}

export async function clearAdminMobileNumberForUser(userId: string): Promise<void> {
  const { error } = await supabase.from('admin_mobile_numbers').delete().eq('user_id', userId);
  if (error) throw error;
}
